// MilestonesCard — payment-milestone position: how much of the milestone
// value has been completed, and which milestones need attention now.
//
// Two honesty rules shape this card:
//
//   1. "Value completed" is acceptance, not payment. A milestone can be
//      accepted without being paid, and the system holds no settlement
//      record, so "Paid to date" would be a claim the data cannot make.
//   2. Rows render their DISPLAY status — lifecycle plus risk overlay. An
//      undelivered milestone's lifecycle stays 'upcoming' no matter how far
//      past its planned date it is, so a bare lifecycle pill would tell the
//      reader the opposite of the truth about the one row they most need to
//      act on.
//   3. `totalCount` counts EVERY milestone on the contract, while `rows`
//      carries only the overdue and next-30-days subset. The count is
//      therefore a metric in its own right and never the table's
//      denominator — which is why the truncation note here quotes no
//      denominator at all, unlike the issues and insurance sections.

import MetricCard from "../tracking/deliverable/MetricCard";
import TrackingStatusBadge from "../tracking/shared/TrackingStatusBadge";
import SlipTag from "../tracking/milestone/SlipTag";
import ReportSection, {
  NothingToReport,
  UnavailableNotice,
} from "./ReportSection";
import { countTone, formatPercent } from "./reportFormat";
import { formatAud2dp } from "../../../utils/formatCurrency";
import { formatDayMonthYear } from "../../../utils/formatShortDate";
import type {
  SectionUnavailable,
  SummaryMilestoneRow,
  SummaryMilestonesSection,
} from "../../../types/report";

const SECTION_LABEL = "Milestones";

export interface MilestonesCardProps {
  section: SummaryMilestonesSection | SectionUnavailable;
}

export default function MilestonesCard({ section }: MilestonesCardProps) {
  if (section.status === "unavailable") {
    return (
      <ReportSection id="milestones" title={SECTION_LABEL}>
        <UnavailableNotice label={SECTION_LABEL} />
      </ReportSection>
    );
  }

  if (section.totalCount === 0) {
    return (
      <ReportSection id="milestones" title={SECTION_LABEL}>
        <NothingToReport />
      </ReportSection>
    );
  }

  return (
    <ReportSection id="milestones" title={SECTION_LABEL}>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <MetricCard
          testId="milestones-value-completed"
          label="Value completed"
          value={formatAud2dp(section.valueCompleted)}
          breakdown={
            <ValueCompletedBar percent={section.valueCompletedPct} />
          }
          subtitle={`${formatPercent(section.valueCompletedPct)} of ${formatAud2dp(section.valueTotal)} ex-GST · ${section.acceptedCount} accepted`}
        />
        <MetricCard
          testId="milestones-total"
          label="Milestones"
          value={section.totalCount}
          subtitle="on this contract"
        />
        <MetricCard
          testId="milestones-slipped"
          label="Slipped"
          value={section.slippedCount}
          valueTone={countTone(section.slippedCount, "warning")}
          subtitle={
            section.worstSlipDays === null
              ? undefined
              : `worst slip ${section.worstSlipDays} days`
          }
        />
        <MetricCard
          testId="milestones-due-30"
          label="Due next 30 days"
          value={section.dueNext30Days}
          valueTone={countTone(section.dueNext30Days, "brand")}
        />
      </div>

      {section.rows.length === 0 ? (
        <p className="mt-4 text-body-sm text-slate-500 dark:text-slate-400">
          No milestones overdue or due in the next 30 days
        </p>
      ) : (
        <>
          <div
            className="mt-4 overflow-x-auto"
            tabIndex={0}
            role="group"
            aria-label="Milestones needing attention"
          >
            <table className="w-full">
              <caption className="sr-only">
                Milestones needing attention — overdue or due in the next 30
                days
              </caption>
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800">
                  <th className="px-3 py-2 text-left text-overline uppercase text-slate-600 dark:text-slate-400">
                    No.
                  </th>
                  <th className="px-3 py-2 text-left text-overline uppercase text-slate-600 dark:text-slate-400">
                    Milestone
                  </th>
                  <th className="px-3 py-2 text-left text-overline uppercase text-slate-600 dark:text-slate-400">
                    Planned
                  </th>
                  <th className="px-3 py-2 text-left text-overline uppercase text-slate-600 dark:text-slate-400">
                    Status
                  </th>
                  <th className="px-3 py-2 text-right text-overline uppercase text-slate-600 dark:text-slate-400">
                    Payment
                  </th>
                </tr>
              </thead>
              <tbody>
                {section.rows.map((row) => (
                  <MilestoneRow key={row.id} row={row} />
                ))}
              </tbody>
            </table>
          </div>
          {section.truncated && (
            <p
              data-testid="milestones-truncation-note"
              className="mt-2 text-caption text-slate-500 dark:text-slate-400"
            >
              Showing the first 50 milestones needing attention — open the
              Milestones tab for the full list
            </p>
          )}
        </>
      )}
    </ReportSection>
  );
}

function MilestoneRow({ row }: { row: SummaryMilestoneRow }) {
  return (
    <tr className="border-b border-slate-100 dark:border-slate-700">
      <td
        data-testid={`milestone-number-${row.id}`}
        className="px-3 py-2 text-body-sm tabular-nums text-slate-500 dark:text-slate-400"
      >
        {row.milestoneNumber === null
          ? "—"
          : `${row.milestoneNumber}${row.milestoneSuffix ?? ""}`}
      </td>
      <td className="px-3 py-2 text-body-sm text-slate-900 dark:text-slate-100">
        {row.name}
      </td>
      <td className="px-3 py-2 text-body-sm tabular-nums text-slate-900 dark:text-slate-100">
        {formatDayMonthYear(row.plannedDate)}
      </td>
      <td className="px-3 py-2">
        <span
          data-testid={`milestone-status-${row.id}`}
          className="inline-flex flex-wrap items-center gap-1"
        >
          {/* The DISPLAY status, not the bare lifecycle: an undelivered
              milestone's lifecycle is 'upcoming' however overdue it is, and
              the badge's overlay handling is what turns that into the red
              "Late" pill a reader needs to see. */}
          <TrackingStatusBadge kind="milestone" status={row.displayStatus} />
          {row.daysOverdue !== null && (
            <SlipTag variant="late" days={row.daysOverdue} />
          )}
        </span>
      </td>
      <td
        data-testid={`milestone-payment-${row.id}`}
        className="px-3 py-2 text-right text-body-sm tabular-nums text-slate-900 dark:text-slate-100"
      >
        {row.paymentAmount === null ? "—" : formatAud2dp(row.paymentAmount)}
      </td>
    </tr>
  );
}

/** Completion proportion. Omitted when the server could not derive one — a
 *  bar rendered at zero would read as "nothing done" rather than "unknown". */
function ValueCompletedBar({ percent }: { percent: number | null }) {
  if (percent === null) return null;
  const clamped = Math.min(100, Math.max(0, percent));
  return (
    <div
      role="progressbar"
      aria-valuenow={percent}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={`Value completed: ${Math.round(percent)} percent`}
      className="mt-2 h-1 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700"
    >
      <div
        className="h-full rounded-full bg-emerald-600 dark:bg-emerald-400"
        style={{ width: `${clamped}%` }}
      />
    </div>
  );
}
